import { useEffect, useRef, useState, type ReactNode } from "react";

export function ScrollableTabs({
  ariaLabel,
  children,
  className,
}: {
  ariaLabel: string;
  children: ReactNode;
  className?: string;
}) {
  const region = useRef<HTMLDivElement>(null);
  const [edges, setEdges] = useState({ start: true, end: true });

  const updateEdges = () => {
    const element = region.current;
    if (!element) return;
    setEdges({
      start: element.scrollLeft <= 1,
      end: element.scrollLeft + element.clientWidth >= element.scrollWidth - 1,
    });
  };

  const revealed = useRef<HTMLElement | null>(null);

  // Children change on every parent render, and the Action Centre re-renders
  // every 15 seconds when its counts refresh. So this runs often, and must
  // only ever move the strip sideways, and only when the active tab changes.
  // It used `scrollIntoView`, which also scrolls the page vertically to bring
  // the tab bar into view: reading down a long queue, the page jumped back to
  // the top on every refresh.
  useEffect(() => {
    const element = region.current;
    if (!element) return;
    updateEdges();
    const active = element.querySelector<HTMLElement>(
      '[aria-selected="true"], [aria-current="page"], .active',
    );
    if (!active || active === revealed.current) return;
    revealed.current = active;

    const strip = element.getBoundingClientRect();
    const tab = active.getBoundingClientRect();
    const offset =
      tab.left < strip.left
        ? tab.left - strip.left
        : tab.right > strip.right
          ? tab.right - strip.right
          : 0;
    if (offset !== 0) element.scrollBy?.({ left: offset, behavior: "smooth" });
  }, [children]);

  useEffect(() => {
    const element = region.current;
    if (!element || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(updateEdges);
    observer.observe(element);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      className={`ui-tabs-overflow${edges.start ? " is-at-start" : ""}${edges.end ? " is-at-end" : ""}`}
    >
      <div
        ref={region}
        className={`ui-tabs-scroll-region${className ? ` ${className}` : ""}`}
        role="tablist"
        aria-label={ariaLabel}
        aria-orientation="horizontal"
        onScroll={updateEdges}
      >
        {children}
      </div>
    </div>
  );
}
